/**
 * Anon share-back — the record + message machinery for the Anon giving their
 * name and one contact to the host. LIFTED out of AnonConnectionPage
 * (tiered-exchange Plan 1, Step 3 / A5) so the sans-chat flow can share
 * without mounting chat; the chat page delegates here, so there is ONE copy.
 *
 * Race rule (CLAUDE-PROJECT § Deterministic-ID Data Messages, stated
 * precisely): TWO contact data messages fired in ONE action upsert the same
 * deterministic row and race — that's what wiped the host's copy
 * (f/anon-contact-share, 2026-06-30: a name-only message and the real one in
 * the same submit). One authoritative write PER STATE CHANGE is fine across
 * SEPARATE user actions: announceAnonName writes at the name screen's Next,
 * commitAnonContactRecord at the contact screen's Next — user-paced seconds
 * apart, and the later write carries a superset of the earlier one, so even
 * the upsert order is harmless. Never fire both from a single handler.
 */

import { getAnonData, saveAnonData, saveContactSharingStatus } from '../storage/userDataStorage';
import { sendMessage } from '../chat/chatService';
import { buildAnonContactDataMessage } from '../chat/messageBuilders';
import { getOrCreateAnonUserId } from '../../utils/identityUtils';

// anonDataUpdated CustomEvent — ONE construction site (refactor log 2026-07-16:
// three drifted copies were the "one part of the screen updated, another
// didn't" bug class)
export const dispatchAnonDataUpdated = (chatRoomId, anonData) => {
  window.dispatchEvent(new CustomEvent('anonDataUpdated', { detail: { chatRoomId, anonData } }));
};

// Persist the anon's own name (merge-preserving) + notify listeners — shared by
// the chat first-share/edit paths and the sans-chat name screen (refactor log
// 2026-07-15: two paths carried drifted copies of this block)
export const persistAnonName = async (chatRoomId, name) => {
  const existingData = (await getAnonData(chatRoomId)) || {};
  const anonData = { ...existingData, name };
  if (!anonData.sharedFields) anonData.sharedFields = [];
  await saveAnonData(anonData, chatRoomId);
  dispatchAnonDataUpdated(chatRoomId, anonData);
  return anonData;
};

/**
 * Announce the name to the host — the name screen's Next (user ruling, device
 * pass 1 2026-08-08): the host's waiting card becomes a named card while the
 * person is still on the contact screen, opening the venue confirm/edit
 * window early. Persists locally + sends ONE contact data message carrying
 * the name (sharedFields untouched — nothing is granted by naming yourself).
 * Worst case (abandon after this): the host keeps a named, contact-less card.
 */
export const announceAnonName = async (chatRoomId, name) => {
  const anonData = await persistAnonName(chatRoomId, name);
  sendMessage(
    chatRoomId,
    buildAnonContactDataMessage({ senderId: getOrCreateAnonUserId(), anonData })
  );
  return anonData;
};

/**
 * Record-commit for a classified contact — ADDITIVE (decision #15, both
 * directions): a new type joins the record, a same-type re-share updates its
 * value, nothing is ever un-shared. Sends the ONE authoritative `contact`
 * data message per action (deterministic id assigned in chatService) — this
 * message must keep flowing: conversion identity-healing reads self-declared
 * phones from `contact` messages (migration 021).
 *
 * @param {string} chatRoomId
 * @param {string} contactType - classified type ('phone', 'instagram', ...)
 * @param {string} contactValue
 * @param {Object} [opts]
 * @param {string} [opts.fallbackName] - name to stamp when the record has none
 * @returns {Object|null} the updated anonData, or null on an identical re-share
 */
export const commitAnonContactRecord = async (
  chatRoomId,
  contactType,
  contactValue,
  { fallbackName = '' } = {}
) => {
  const existingData = (await getAnonData(chatRoomId)) || {};
  const existingSharedFields = existingData.sharedFields || [];

  // Identical re-share: nothing to change on the record
  if (existingData[contactType] === contactValue && existingSharedFields.includes(contactType)) {
    return null;
  }

  const anonData = {
    ...existingData,
    name: existingData.name || fallbackName,
    [contactType]: contactValue,
    contactType,
    sharedFields: [...new Set([...existingSharedFields, contactType])],
  };

  // Data-only — any speech bubble is a separate chat-flow concern
  sendMessage(
    chatRoomId,
    buildAnonContactDataMessage({ senderId: getOrCreateAnonUserId(), anonData, contactType })
  );

  await saveAnonData(anonData, chatRoomId);
  dispatchAnonDataUpdated(chatRoomId, anonData);
  await saveContactSharingStatus(chatRoomId, true);

  return anonData;
};
